type DetectionSignal = {
  id: string;
  severity: "low" | "medium" | "high" | "critical";
  score: number;
  reason: string;
  evidence?: string;
};

type UrlFinding = {
  raw: string;
  hostname: string | null;
  domain: string | null;
  publicSuffix: string | null;
  domainTrust: "trusted" | "unknown" | "suspicious";
  scheme: string | null;
  path: string;
  signals: DetectionSignal[];
};

type DetectionResult = {
  label: "safe" | "suspicious" | "phishing";
  action: "allow" | "warn" | "block" | "manual_review";
  riskScore: number;
  confidence: number;
  languages: string[];
  urls: UrlFinding[];
  signals: DetectionSignal[];
  reasons: string[];
  latencyMs: number;
};

const samples = [
  "Dear user, your ConnectlPS account is locked for security purpose. Please update by visiting https://connecctiips.com/ to secure it.",
  "Your parcel 50826971613 from trimurti clearance has arrived at destination. We will call you for delivery. Aramex Courier 015924302 https://eswa.io/L7G6ME5",
  "तपाईंको खाता बन्द हुनेछ। KYC प्रमाणित गर्न https://khalti-secure.click/login खोल्नुहोस्",
  "Ncell notice: your data pack has been renewed successfully. Thank you.",
];

const form = getElement<HTMLFormElement>("detect-form");
const textarea = getElement<HTMLTextAreaElement>("message");
const charCount = getElement<HTMLSpanElement>("char-count");
const sampleButton = getElement<HTMLButtonElement>("sample-button");
const emptyState = getElement<HTMLDivElement>("empty-state");
const resultView = getElement<HTMLDivElement>("result");
const verdictCard = getElement<HTMLDivElement>("verdict-card");
const scoreRing = getElement<HTMLDivElement>("score-ring");
const label = getElement<HTMLHeadingElement>("label");
const action = getElement<HTMLParagraphElement>("action");
const score = getElement<HTMLSpanElement>("score");
const confidence = getElement<HTMLElement>("confidence");
const languages = getElement<HTMLElement>("languages");
const urlCount = getElement<HTMLElement>("url-count");
const latency = getElement<HTMLSpanElement>("latency");
const urls = getElement<HTMLDivElement>("urls");
const signals = getElement<HTMLDivElement>("signals");
const rawJson = getElement<HTMLPreElement>("raw-json");

textarea.addEventListener("input", updateCharacterCount);
sampleButton.addEventListener("click", () => {
  textarea.value = samples[Math.floor(Math.random() * samples.length)] ?? samples[0]!;
  updateCharacterCount();
  textarea.focus();
});

form.addEventListener("submit", async (event) => {
  event.preventDefault();
  const message = textarea.value.trim();
  if (!message) return;

  setLoading(true);

  try {
    const response = await fetch("/detect-sms", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ message }),
    });

    const body = await response.json() as DetectionResult | { error: string };
    if (!response.ok || "error" in body) throw new Error("error" in body ? body.error : "Detection failed");

    renderResult(body);
  } catch (error) {
    renderError(error instanceof Error ? error.message : "Unable to analyze message");
  } finally {
    setLoading(false);
  }
});

updateCharacterCount();

function renderResult(result: DetectionResult): void {
  emptyState.classList.add("hidden");
  resultView.classList.remove("hidden");
  verdictCard.className = `verdict-card ${result.label}`;
  scoreRing.style.setProperty("--score", `${result.riskScore * 3.6}deg`);

  label.textContent = titleCase(result.label);
  action.textContent = `Action: ${result.action.replace("_", " ")}`;
  score.textContent = String(result.riskScore);
  confidence.textContent = `${Math.round(result.confidence * 100)}%`;
  languages.textContent = result.languages.join(", ");
  urlCount.textContent = String(result.urls.length);
  latency.textContent = `${result.latencyMs}ms`;
  rawJson.textContent = JSON.stringify(result, null, 2);

  urls.replaceChildren(...renderUrls(result.urls));
  signals.replaceChildren(...renderSignals(result.signals));
}

function renderUrls(items: UrlFinding[]): HTMLElement[] {
  if (items.length === 0) return [emptyLine("No URLs found in this SMS.")];

  return items.map((item) => {
    const card = document.createElement("article");
    card.className = `url-card ${item.domainTrust}`;
    card.innerHTML = `
      <div>
        <strong>${escapeHtml(item.domain ?? item.hostname ?? "Unknown host")}</strong>
        <span>${escapeHtml(item.raw)}</span>
      </div>
      <b>${item.domainTrust}</b>
    `;
    return card;
  });
}

function renderSignals(items: DetectionSignal[]): HTMLElement[] {
  if (items.length === 0) return [emptyLine("No risky signals found.")];

  return items.map((item) => {
    const row = document.createElement("article");
    row.className = `signal ${item.severity}`;
    row.innerHTML = `
      <span>${item.severity}</span>
      <div>
        <strong>${escapeHtml(item.reason)}</strong>
        <small>${escapeHtml(item.id)} · +${item.score}${item.evidence ? ` · ${escapeHtml(item.evidence)}` : ""}</small>
      </div>
    `;
    return row;
  });
}

function renderError(message: string): void {
  emptyState.classList.add("hidden");
  resultView.classList.remove("hidden");
  verdictCard.className = "verdict-card suspicious";
  label.textContent = "Error";
  action.textContent = message;
  score.textContent = "!";
  confidence.textContent = "0%";
  languages.textContent = "unknown";
  urlCount.textContent = "0";
  latency.textContent = "Failed";
  urls.replaceChildren(emptyLine("No URL data."));
  signals.replaceChildren(emptyLine(message));
  rawJson.textContent = JSON.stringify({ error: message }, null, 2);
}

function setLoading(isLoading: boolean): void {
  form.classList.toggle("loading", isLoading);
  form.querySelector<HTMLButtonElement>("button[type='submit']")!.disabled = isLoading;
  latency.textContent = isLoading ? "Analyzing..." : latency.textContent;
}

function updateCharacterCount(): void {
  charCount.textContent = `${textarea.value.length} characters`;
}

function emptyLine(text: string): HTMLElement {
  const element = document.createElement("p");
  element.className = "muted-line";
  element.textContent = text;
  return element;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[char]!);
}

function getElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing element: ${id}`);
  return element as T;
}
